import React, { useEffect, useRef, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import DefaultScene from '../three/DefaultScene';
import MonoScene from '../three/MonoScene';
import ARHeadScene from '../three/ARHeadScene';
import PhoneScene from '../three/PhoneScene';
import { trackEvent } from '../analytics';
import { PlaceholderImage, PlaceholderVideo } from './PlaceholderMedia';

const sceneMap = {
  default: DefaultScene,
  mono: MonoScene,
  arHead: ARHeadScene,
  phone: PhoneScene
};

/** Wrapper that tracks when a Three.js scene becomes visible */
const ThreeSceneWrapper = ({ className, Scene, media }) => {
  const containerRef = useRef(null);
  const hasTrackedView = useRef(false);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(1);
  const [expanded, setExpanded] = useState(false);
  const collapseTimer = useRef(null);

  const scheduleCollapse = () => {
    clearTimeout(collapseTimer.current);
    collapseTimer.current = setTimeout(() => setExpanded(false), 3000);
  };

  useEffect(() => () => clearTimeout(collapseTimer.current), []);

  const toggleMute = () => {
    if (muted) {
      trackEvent('3D Scene', 'unmute', media.sceneId || 'default');
    }
    setMuted(!muted);
    setExpanded(true);
    scheduleCollapse();
  };

  const handleVolume = (event) => {
    const next = Number(event.target.value);
    setVolume(next);
    setMuted(next === 0);
    scheduleCollapse();
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedView.current) {
          hasTrackedView.current = true;
          trackEvent('3D Scene', 'view', media.sceneId || 'default');
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [media.sceneId]);

  return (
    <div ref={containerRef} className={className} style={{ touchAction: 'none', position: 'relative' }}>
      <Canvas
        dpr={[1, 2]}
        gl={{ powerPreference: 'high-performance', alpha: false }}
        style={{ width: '100%', height: '100%' }}
        camera={{ position: [2.4, 1.8, 2.4], fov: 45 }}
      >
        <Scene videoPath={media.videoPath} muted={muted} volume={volume} />
      </Canvas>
      {media.audio ? (
        <div
          className={`scene-audio-control${expanded ? ' scene-audio-control--expanded' : ''}`}
          onMouseEnter={() => {
            clearTimeout(collapseTimer.current);
            setExpanded(true);
          }}
          onMouseLeave={() => setExpanded(false)}
        >
          <button
            type="button"
            className="scene-audio-toggle"
            aria-label={muted ? 'Unmute video' : 'Mute video'}
            onClick={toggleMute}
          >
          {muted ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
          )}
          </button>
          <input
            type="range"
            className="scene-audio-slider"
            min="0"
            max="1"
            step="0.01"
            value={muted ? 0 : volume}
            style={{ '--fill': `${(muted ? 0 : volume) * 100}%` }}
            aria-label="Volume"
            tabIndex={expanded ? 0 : -1}
            onChange={handleVolume}
          />
        </div>
      ) : null}
    </div>
  );
};

const WorkMedia = ({ media, className = '' }) => {
  if (!media) {
    return null;
  }

  if (media.type === 'video') {
    return (
      <PlaceholderVideo
        className={className}
        src={media.src}
        poster={media.poster}
        playsInline
        autoPlay
        muted
        loop
      >
        Your browser does not support the video tag.
      </PlaceholderVideo>
    );
  }

  if (media.type === 'three') {
    const Scene = sceneMap[media.sceneId] || DefaultScene;
    return <ThreeSceneWrapper className={className} Scene={Scene} media={media} />;
  }

  return <PlaceholderImage className={className} src={media.src} alt={media.alt || ''} loading="lazy" />;
};

export default WorkMedia;
